import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	Input,
	Label,
} from 'reactstrap';

import {
	DangerAlert,
	IconButton,
	Loading,
	OAUTH_PROVIDERS,
	OAUTH_TYPES,
	useDebounce,
	useLazyLoad,
} from '@techchange/components';
import { messages } from '../../i18n';

import MiroBoardCard from './MiroBoardCard';
import NewMiroBoardCard from './NewMiroBoardCard';

import './MiroBoardSelector.scss';

const SEARCH_TIMEOUT = 350;
const MIRO_BOARDS_NODE_ID = 'tc-miro-boards-wrapper';
const MIRO_BOARDS_FETCH_LIMIT = 20;

const propTypes = {

	/**
	 * The oAuth Miro integration.
	 */
	integration: PropTypes.shape({

		/**
		 * The integration ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The integration provider.
		 */
		provider: PropTypes.oneOf([OAUTH_PROVIDERS.MIRO]).isRequired,

		/**
		 * The integration type.
		 */
		type: PropTypes.oneOf([OAUTH_TYPES.API]).isRequired,

	}).isRequired,

	/**
	 * The function for the click event to close the Miro board selector.
	 */
	onCancel: PropTypes.func.isRequired,

	/**
	 * The function for the click event for the Miro board cards.
	 */
	onSelectBoard: PropTypes.func.isRequired,

	/**
	 * The function used to make API requests through the TechChange oAuth API for Miro board cards.
	 */
	onUseAPI: PropTypes.func.isRequired,

};

const MiroBoardSelector = (props) => {

	const { integration, onCancel, onSelectBoard, onUseAPI } = props;

	const [miroBoards, setMiroBoards] = useState(null);
	const [postError, setPostError] = useState(false);
	const [boardError, setBoardError] = useState(false);
	const [search, setSearch] = useState('');
	const [loadState, setLoadState] = useLazyLoad(MIRO_BOARDS_NODE_ID, {
		offset: 0,
		scrollMargin: 500,
		listenToWindow: true,
	});
	const debouncedSearchText = useDebounce(search, SEARCH_TIMEOUT);

	const intl = useIntl();

	// Trigger load on initial render or when search text changes.
	useEffect(() => {

		setLoadState((prev) => Object.assign({}, prev, {
			offset: 0,
			loading: true,
			ready: true,
			loadingNextBatch: false,
			loadedAll: false,
		}));

	}, [debouncedSearchText, setLoadState]);

	useEffect(() => {

		if (integration) {

			// Bail early if we don't need to fetch the next batch of Miro boards
			if (!loadState.loading) {
				return;
			}

			// Bail early if we've already started loading the next batch of Miro boards
			if (loadState.loadingNextBatch) {
				return;
			}

			// Otherwise initiate loading process
			setLoadState((prev) => Object.assign({}, prev, {
				loadingNextBatch: true,
			}));

			setBoardError(false);

			onUseAPI(integration, {
				url: '/v2/boards',
				method: 'get',
				params: {
					limit: 50,
					query: debouncedSearchText
				},
				data: null,
			})
				.then((response) => {

					if (response && response.data) {

						setMiroBoards((prev) => {

							if (loadState.offset === 0) {
								return response.data.data;
							}

							return prev.concat(response.data.data);

						});

						setLoadState((prev) => Object.assign({}, {
							offset: prev.offset + MIRO_BOARDS_FETCH_LIMIT,
							loading: false,
							loadingNextBatch: false,
							loadedAll: response.data.size < MIRO_BOARDS_FETCH_LIMIT,
							ready: true,
						}));

					}

				})
				.catch((error) => {
					console.error(error);
					setBoardError(true);
					setLoadState((prev) => Object.assign({}, prev, {
						loading: false,
						ready: false,
					}));
				});

		}

	}, [
		integration,
		debouncedSearchText,
		loadState.loading,
		loadState.offset,
		loadState.loadingNextBatch,
		setLoadState,
		onUseAPI,
	]);

	const onCreateBoard = () => {

		onUseAPI(integration, {
			url: '/v2/boards',
			method: 'post',
			params: null,
			data: {
				description: 'New Miro board',
				name: 'Untitled'
			}
		})
			.then((response) => {

				if (response && response.data) {
					onSelectBoard(response.data);
				}

			})
			.catch((error) => {
				console.error(error);
				setPostError(true);
			});
	};

	return (
		<div
			className="d-flex flex-column flex-wrap align-items-center
			tc-miro-board-selector-container">
			<h1 className="mb-4">{intl.formatMessage(messages.TC_COMPONENTS_MIRO_BOARD_SELECTOR_TITLE)}</h1>
			{postError &&
				<DangerAlert
					dismissible={false}>
					<span>
						{intl.formatMessage(messages.TC_COMPONENTS_MIRO_BOARD_SELECTOR_NEW_BOARD_ERROR)}
					</span>
				</DangerAlert>
			}
			{boardError &&
				<DangerAlert
					dismissible={false}>
					<span>
						{intl.formatMessage(messages.TC_COMPONENTS_MIRO_BOARD_SELECTOR_GET_BOARDS_ERROR)}
					</span>
				</DangerAlert>
			}
			<div className="tc-miro-board-selector-searchbar mb-4">
				<Label
					className="sr-only"
					for="search">
					{intl.formatMessage(messages.TC_COMPONENTS_MIRO_BOARD_SELECTOR_SEARCH_BAR_PLACEHOLDER_TEXT)}
				</Label>
				<Input
					type="text"
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_MIRO_BOARD_SELECTOR_SEARCH_BAR_PLACEHOLDER_TEXT)}
					value={search}
					name="search"
					onChange={(event) => setSearch(event.target.value)} />
			</div>
			{!boardError &&
				<Loading
					className="loading-container"
					isLoading={loadState.loadingNextBatch}>
					{miroBoards &&
						<div
							id={MIRO_BOARDS_NODE_ID}
							className="tc-miro-board-selector-boards-list">
							<NewMiroBoardCard onCreateBoard={onCreateBoard} />
							{miroBoards.map((board) => (
								<MiroBoardCard
									key={board.id}
									board={board}
									onSelectBoard={onSelectBoard} />))}
						</div>
					}
				</Loading>
			}
			<IconButton
				// NOTE: Absolutely positioned in the top-right corner of the selector
				id="tc-miro-board-selector-icon-btn-close"
				className="tc-miro-board-selector-close-btn"
				icon="fas fa-xmark fa-xl"
				size="38px"
				title={intl.formatMessage(messages.TC_COMPONENTS_MIRO_BOARD_SELECTOR_CLOSE_BTN_LABEL)}
				placement="top"
				tooltip
				onClick={onCancel} />
		</div>
	);

};

MiroBoardSelector.propTypes = propTypes;

export default MiroBoardSelector;
